import React, {useState, useEffect, useContext, ReactNode} from 'react'
import {toggle} from 'opticks/lib/optimizely'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import {parse} from 'qs'
import {History} from 'history'
import {setAudienceTokenInBrowser} from '../modules/actions'
import {getUrlPathName} from '../utils/url'
import {
  getLastLoginUrl,
  removeLastLoginUrl,
  getUserTierFromCookie,
  persistUserTierOnCookie,
  removeUserTierOnCookie,
  removeUserLastConnectionMethodCookie,
  getUserLastConnectionMethodFromCookie
} from '../business/persistData'
import {getAuthClient, AuthClientType} from '../services'
import {showPasswordlessLogin} from '../modules/slice'

import {
  AuthenticationFlows,
  ShowPasswordlessLoginPayload,
  AuthContextType
} from '../types/Auth'

import {
  hasAudienceTokenInBrowser,
  hasAudienceTokenInUrl,
  SIGN_UP_TOKEN,
  USER_TIERS,
  hasExpectedGsiteValueInUrl
} from '../../offer/business/privateDeals'

import {removeURLParameter} from '../../utils/url'
import {BrandType} from 'brand/types'
import {SILENT_AUTH_URL_TRIGGER} from '../constants'
import {User} from '@auth0/auth0-spa-js'
import {ActionCreatorWithOptionalPayload} from '@reduxjs/toolkit'
import {
  getUserLastCheckoutDate,
  getUserTier,
  hasPlusTier
} from '../modules/selectors'
import {includes} from 'ramda'
import {updateUserMetadata} from '../../user/services'
import {isFindHotelBrand} from '../../customerSupport/business'
import {useIsMemberPlus} from '../modules/useCognito'

export type AuthProviderType = {
  audience: string
  authEnabled: boolean
  children: ReactNode
  client_id: string
  domain: string
  history: History
  showPasswordlessLogin: ActionCreatorWithOptionalPayload<ShowPasswordlessLoginPayload>
  setAudienceTokenInBrowser: ActionCreatorWithOptionalPayload<string>
  useRefreshTokens: boolean
  authorizeTimeoutInSeconds: number
  sessionCheckExpiryDays: number
  brand: BrandType
  enabledBrands: Array<BrandType['code']>
}

const cleanUpSSOTrigger = () => {
  const url = removeURLParameter(window.location.href, SILENT_AUTH_URL_TRIGGER)

  window.history.replaceState({}, document.title, url)
}

export const AuthContext = React.createContext<Partial<AuthContextType>>({})

export const useAuth = () => useContext<Partial<AuthContextType>>(AuthContext)

export function withAuth<P extends AuthContextType>(
  Component: React.ComponentType<P>
) {
  return (props: P) => {
    return (
      <AuthContext.Consumer>
        {(auth: AuthContextType) => <Component {...props} auth={auth} />}
      </AuthContext.Consumer>
    )
  }
}

export const getIsEmployee = (user?: User) => {
  const userTierFromCookie = getUserTierFromCookie()
  const userTierFromMetaData = (user && getUserTier(user)) || []
  const userTier =
    userTierFromCookie.length > 0 ? userTierFromCookie : userTierFromMetaData

  return (
    includes(USER_TIERS.employee, userTier) ||
    includes(USER_TIERS.friend, userTier)
  )
}

export const getIsMemberPlus = () => {
  const userTierFromCookie = getUserTierFromCookie()
  return hasPlusTier(userTierFromCookie) || false
}

const AuthProvider = ({
  children,
  showPasswordlessLogin,
  setAudienceTokenInBrowser,
  history,
  brand,
  enabledBrands,
  audience,
  client_id,
  domain,
  useRefreshTokens,
  authorizeTimeoutInSeconds,
  sessionCheckExpiryDays
}: AuthProviderType) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User>()
  const [lastCheckoutDate, setLastCheckoutDate] = useState<string>()
  const [authClient, setAuthClient] = useState<AuthClientType>()
  const [isLoading, setIsLoading] = useState(true)
  const [isAudience, setIsAudience] = useState(false)
  const [loginPathName, setLoginPathName] = useState<string | null>()

  const authEnabled = enabledBrands.includes(brand?.code)
  const {location} = history
  const isMemberPlus = useIsMemberPlus()
  const isEmployee = getIsEmployee(user)
  const urlParams = parse(history?.location?.search, {
    ignoreQueryPrefix: true
  })

  // Init Auth0
  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await getAuthClient({
        audience,
        client_id,
        domain,
        useRefreshTokens,
        authorizeTimeoutInSeconds,
        sessionCheckExpiryDays
      })
      setAuthClient(auth0FromHook)

      const user = await auth0FromHook.getUser()

      setUser(user)
      setIsAuthenticated(Boolean(user))
      setIsLoading(false)
    }

    if (authEnabled) {
      void initAuth0()
    }
  }, [])

  // SSO login
  useEffect(() => {
    const ssoLogin = async () => {
      try {
        setIsLoading(true)
        await authClient.getTokenSilently()
        const user = await authClient.getUser()
        setUser(user)
        setIsAuthenticated(Boolean(user))
        cleanUpSSOTrigger()
      } catch (error) {
        console.log(error)
        // Skip
      } finally {
        setIsLoading(false)
      }
    }

    if (
      !user &&
      authClient &&
      location.search.includes(SILENT_AUTH_URL_TRIGGER)
    ) {
      void ssoLogin()
    }
  }, [authClient, location])

  // Get audience from URL
  useEffect(() => {
    const hasAudienceToken = hasAudienceTokenInUrl(urlParams)

    setIsAudience(hasAudienceToken)

    if (hasAudienceToken) {
      const token = urlParams.token as string
      setAudienceTokenInBrowser(token || SIGN_UP_TOKEN)
    }
  }, [location.search, urlParams])

  // Get audience from Browser
  useEffect(() => {
    setIsAudience(hasAudienceTokenInBrowser())
  }, [])

  // Remove user cookies in case logged out
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      removeUserTierOnCookie()
    }
  }, [isAuthenticated, user, isLoading])

  // Set last login page and last checkout date
  useEffect(() => {
    if (isAuthenticated) {
      const pathName = getUrlPathName(getLastLoginUrl())
      const userLastCheckoutDate = getUserLastCheckoutDate(user)
      const lastConnectionMethod = getUserLastConnectionMethodFromCookie()

      setLoginPathName(pathName)
      setLastCheckoutDate(userLastCheckoutDate)
      if (lastConnectionMethod && isFindHotelBrand(brand?.code))
        void updateUserMetadata({lastConnectionMethod})
    }
  }, [isAuthenticated, user])

  // Persist user tiers in cookies
  useEffect(() => {
    persistUserTierOnCookie(user)
  }, [user])

  const handleLogIn = ({
    sourceComponent,
    flow,
    anonymousId
  }: ShowPasswordlessLoginPayload) => {
    const isSmsFlowEnabled = toggle('aded6ba5-sms-flow-enabled', false, true)

    // In case SMS flow is disabled fallback to SignUp
    const authFlow =
      flow === AuthenticationFlows.UnlockOverlay && !isSmsFlowEnabled
        ? AuthenticationFlows.SignUp
        : flow

    showPasswordlessLogin({
      anonymousId,
      sourceComponent,
      flow: authFlow || AuthenticationFlows.Login
    })
  }

  const handleLogOut = () => {
    removeUserTierOnCookie()
    removeLastLoginUrl()
    removeUserLastConnectionMethodCookie()

    const url = window.location.href
    void authClient.logout({returnTo: url})
  }

  const value = authEnabled
    ? {
        authEnabled,
        isAuthenticated,
        isPrivileged:
          isAuthenticated ||
          isAudience ||
          hasExpectedGsiteValueInUrl(urlParams),
        isAudience,
        user,
        isMemberPlus,
        isEmployee,
        lastCheckoutDate,
        setUser,
        isLoading,
        getIdTokenClaims: async (...p) => authClient.getIdTokenClaims(...p),
        getTokenSilently: async (...p) => authClient.getTokenSilently(...p),
        logIn: handleLogIn,
        logOut: handleLogOut,
        loginPathName,
        accountUrl: `https://${brand.defaultDomain}/user/account`,
        authConnection: {
          clientId: client_id,
          audience,
          domain
        }
      }
    : {
        authEnabled
      }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      showPasswordlessLogin,
      setAudienceTokenInBrowser
    },
    dispatch
  )

const ConnectedAuthProvider = connect(null, mapDispatchToProps)(AuthProvider)

export {ConnectedAuthProvider as AuthProvider}
